/*

  dFree Boilerplate

  Site Development by Badhawk Workshop
  Developer: Daniel Freeman [@dFree]

*/

// Low Bandwidth Mode - must run early to prevent image loading
import './low-bandwidth-mode.js';

//////////////////////////////////////
// ALERT BAR
// Run before DOMContentLoaded so dismissed bars vanish before paint.
(() => {
  const bar = document.querySelector('.site-alert-bar');
  if (!bar) return;

  const alertId     = bar.dataset.alertId;
  const dismissible = bar.dataset.dismissible === '1';
  const storageKey  = `alert_dismissed_${alertId}`;

  const setHeight = (h) => {
    document.documentElement.style.setProperty('--alert-bar-height', `${h}px`);
  };

  const dismiss = () => {
    bar.classList.add('dismissed');
    setHeight(0);
    if (alertId && dismissible) sessionStorage.setItem(storageKey, '1');
  };

  // Honor prior dismissal only when the bar is currently dismissible
  if (dismissible && alertId && sessionStorage.getItem(storageKey)) {
    dismiss();
    return;
  }

  // Measure and expose height
  const measure = () => setHeight(bar.getBoundingClientRect().height);
  measure();
  if ('ResizeObserver' in window) new ResizeObserver(measure).observe(bar);
  else window.addEventListener('resize', measure);

  // Close button (only present when dismissible)
  const closeBtn = bar.querySelector('.close');
  if (closeBtn) closeBtn.addEventListener('click', dismiss);
})();


//////////////////////////////////////
// ALERT MODAL (site-wide pop-up)
document.addEventListener('DOMContentLoaded', () => {
  const modal = document.querySelector('.site-alert-modal');
  if (!modal) return;

  const overlay    = modal.querySelector('.overlay');
  const closeBtn   = modal.querySelector('.close');
  const alertId    = modal.dataset.alertId;
  const storageKey = `alert_modal_dismissed_${alertId}`;

  // Skip if already dismissed this session
  if (alertId && sessionStorage.getItem(storageKey)) return;

  const open = () => {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  const close = () => {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (alertId) sessionStorage.setItem(storageKey, '1');
  };

  if (overlay)  overlay.addEventListener('click', close);
  if (closeBtn) closeBtn.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
  });

  // Auto-open after a tick so the transition runs
  requestAnimationFrame(open);
});


document.addEventListener("DOMContentLoaded", function(){


  //////////////////////////////////////
  // FOOTER ACCORDION (mobile only)
  const CHEVRON_SVG = '<svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7L13 1" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  let footerAccordionActive = false;

  const initFooterAccordions = () => {
    if (footerAccordionActive) return;
    const sections = document.querySelectorAll('.footer-nav .nav-section');

    sections.forEach((section, i) => {
      const h4 = section.querySelector('h4');
      const ul = section.querySelector('ul');
      if (!h4 || !ul) return;

      // Inject chevron icon
      const iconSpan = document.createElement('span');
      iconSpan.className = 'footer-accordion-icon';
      iconSpan.setAttribute('aria-hidden', 'true');
      iconSpan.innerHTML = CHEVRON_SVG;
      h4.appendChild(iconSpan);

      // ARIA
      const panelId = ul.id || 'footer-accordion-panel-' + i;
      ul.id = panelId;
      h4.setAttribute('role', 'button');
      h4.setAttribute('tabindex', '0');
      h4.setAttribute('aria-expanded', 'false');
      h4.setAttribute('aria-controls', panelId);
      ul.setAttribute('aria-hidden', 'true');

      // Collapse
      ul.style.height = '0';
      ul.style.overflow = 'hidden';

      // Click handler
      const handleClick = () => {
        const icon = h4.querySelector('.footer-accordion-icon');
        const isOpen = section.classList.contains('is-open');
        const hasGsap = typeof gsap !== 'undefined';

        if (!hasGsap) {
          // Low-bandwidth / no GSAP fallback
          if (isOpen) {
            ul.style.height = '0';
            section.classList.remove('is-open');
            h4.setAttribute('aria-expanded', 'false');
            ul.setAttribute('aria-hidden', 'true');
          } else {
            section.classList.add('is-open');
            ul.style.height = 'auto';
            h4.setAttribute('aria-expanded', 'true');
            ul.setAttribute('aria-hidden', 'false');
          }
          return;
        }

        // GSAP animation
        gsap.killTweensOf(ul);
        gsap.killTweensOf(icon);

        if (isOpen) {
          gsap.to(ul, {
            height: 0,
            duration: 0.4,
            ease: 'power2.inOut',
            onComplete: () => {
              section.classList.remove('is-open');
              ul.style.height = '0';
            }
          });
          gsap.to(icon, { rotation: 0, duration: 0.4, ease: 'power2.inOut' });
          h4.setAttribute('aria-expanded', 'false');
          ul.setAttribute('aria-hidden', 'true');
        } else {
          section.classList.add('is-open');
          const naturalHeight = ul.scrollHeight;
          gsap.fromTo(ul,
            { height: 0 },
            {
              height: naturalHeight,
              duration: 0.4,
              ease: 'power2.inOut',
              onComplete: () => { ul.style.height = 'auto'; }
            }
          );
          gsap.to(icon, { rotation: 180, duration: 0.4, ease: 'power2.inOut' });
          h4.setAttribute('aria-expanded', 'true');
          ul.setAttribute('aria-hidden', 'false');
        }
      };

      const handleKeydown = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          handleClick();
        }
      };

      h4.addEventListener('click', handleClick);
      h4.addEventListener('keydown', handleKeydown);
      h4._footerClick = handleClick;
      h4._footerKeydown = handleKeydown;
    });

    footerAccordionActive = true;
  };

  const destroyFooterAccordions = () => {
    if (!footerAccordionActive) return;
    const sections = document.querySelectorAll('.footer-nav .nav-section');

    sections.forEach(section => {
      const h4 = section.querySelector('h4');
      const ul = section.querySelector('ul');
      if (!h4 || !ul) return;

      // Kill animations
      if (typeof gsap !== 'undefined') {
        gsap.killTweensOf(ul);
        const icon = h4.querySelector('.footer-accordion-icon');
        if (icon) gsap.killTweensOf(icon);
      }

      // Remove chevron
      const icon = h4.querySelector('.footer-accordion-icon');
      if (icon) icon.remove();

      // Remove ARIA
      h4.removeAttribute('role');
      h4.removeAttribute('tabindex');
      h4.removeAttribute('aria-expanded');
      h4.removeAttribute('aria-controls');
      ul.removeAttribute('aria-hidden');
      if (ul.id && ul.id.startsWith('footer-accordion-panel-')) ul.removeAttribute('id');

      // Reset styles
      ul.style.height = '';
      ul.style.overflow = '';
      section.classList.remove('is-open');

      // Unbind events
      if (h4._footerClick) h4.removeEventListener('click', h4._footerClick);
      if (h4._footerKeydown) h4.removeEventListener('keydown', h4._footerKeydown);
      delete h4._footerClick;
      delete h4._footerKeydown;
    });

    footerAccordionActive = false;
  };

  const footerMql = window.matchMedia('(max-width: 600px)');
  const handleFooterBreakpoint = (e) => {
    if (e.matches) initFooterAccordions();
    else destroyFooterAccordions();
  };
  footerMql.addEventListener('change', handleFooterBreakpoint);
  handleFooterBreakpoint(footerMql);



  //////////////////////////////////////
  // HEADER HEIGHT CSS VAR
  // Exposes --header-height on :root so anchors and sticky offsets stay in sync.
  const headerEl = document.querySelector('header');
  if (headerEl) {
    const setHeaderHeight = () => {
      const h = headerEl.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-height', `${Math.round(h)}px`);
    };
    setHeaderHeight();
    if ('ResizeObserver' in window) {
      new ResizeObserver(setHeaderHeight).observe(headerEl);
    } else {
      window.addEventListener('resize', setHeaderHeight);
    }
  }


  //////////////////////////////////////
  // HEADER SCROLL SHADOW
  const header = document.querySelector('header');
  if (header) {
    const globalHeader = header.querySelector('.global-header');
    const easeInOut = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    const updateShadow = () => {
      if (document.body.classList.contains('mega-menu-open')) {
        globalHeader.style.boxShadow = 'none';
        return;
      }
      const shadowDistance = window.innerHeight * 0.5;
      const progress = Math.min(window.scrollY / shadowDistance, 1);
      const opacity = (0.08 * easeInOut(progress)).toFixed(3);
      globalHeader.style.boxShadow = progress > 0
        ? `0 16px 20px -6px rgba(0,0,0,${opacity})`
        : 'none';
    };
    window.addEventListener('scroll', updateShadow, { passive: true });
    new MutationObserver(updateShadow).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    updateShadow();
  }


});//end